import { IUniqueCustomer } from 'src/generated-types/customer-unique'
import { IUser } from '../generated-types'
import {
  ICustomer,
  ICustomerActivitySummary,
  ICustomerNameWithPermission,
  IOrgCustomer,
} from '../generated-types/customer'
/** Batch item for activity-counts-batch API (customerId + activity counts). */
export interface ICustomerActivityCountsBatchItem extends ICustomerActivitySummary {
  customerId: number
}
import { ILocalityMapDto } from '../generated-types/locality-map-dto'
import { queryClient } from '../query-client'
import { customerQueryKey } from '../query-keys'
import { apiConsts } from './apiConsts'
import { IPaginatedResult } from './paginated-result'
import { useDelete, useGet, usePost } from './reactQuery'
import { IRole } from '@lovoldsystem/authorization.client/types/role'

/** Batch item for activity-counts-batch API (customerId + activity counts). */
export interface ICustomerActivityCountsBatchItem extends ICustomerActivitySummary {
  customerId: number
}

const baseUrl = () => 'customers'
const baseAdminUrl = () => 'admin/customers'

export const customerEndpoints = {
  getUserCustomers() {
    return `${baseUrl()}`
  },
  getCustomerUsers(customerId: EntityId) {
    return `${baseUrl()}/${customerId}/users`
  },
  getCustomerUsersForAdmins(customerId: EntityId) {
    return `${baseUrl()}/${customerId}/admin/users`
  },
  getAdminCustomers(page: number = apiConsts.Page, pageSize: number = apiConsts.PageSize, month?: string) {
    const hasParams = page !== apiConsts.Page || pageSize !== apiConsts.PageSize || month
    if (!hasParams) return baseAdminUrl()
    const params = [`page=${page}`, `pageSize=${pageSize}`]
    if (month) params.push(`month=${encodeURIComponent(month)}`)
    return `${baseAdminUrl()}?${params.join('&')}`
  },

  getUniqueCustomers() {
    return `${baseAdminUrl()}/uniqueCustomers`
  },

  getCustomerAdministrators(customerId: EntityId) {
    return `${baseUrl()}/${customerId}/administrators`
  },
  getCustomerById(id: EntityId) {
    return `${baseUrl()}/${id}`
  },
  getLocalities() {
    return `${baseUrl()}/localities`
  },
  getStorageCustomersByUser() {
    return `${baseUrl()}/storages`
  },
  getCustomersNameWithPermissions() {
    return `${baseUrl()}/name-with-permission`
  },
  getLocalitiesForMap() {
    return `${baseUrl()}/localities-map`
  },
  getLocalityAdministrators(customerId: EntityId) {
    return `${baseUrl()}/${customerId}/locality-administrators`
  },
  deleteAccessBulk(customerId: EntityId) {
    return `${baseUrl()}/${customerId}/users`
  },
  getCustomersServiceStationsAdministrators() {
    return `${baseUrl()}/service-stations/`
  },
  getCustomersActivityCounts(customerId) {
    return `${baseUrl()}/${customerId}/activity-counts/`
  },
  getActivityCountsBatch(customerIds: EntityId[]) {
    if (!customerIds?.length) return `${baseUrl()}/activity-counts-batch`
    return `${baseUrl()}/activity-counts-batch?${customerIds.map(id => `customerIds=${id}`).join('&')}`
  },
  getCustomersWithAdminUserPermission() {
    return `${baseUrl()}/get-customers-with-admin-permission`
  },
  getBulkActivityCounts(customerIds: EntityId[]) {
    return `${baseUrl()}/activity-counts-batch?${customerIds.map(id => `customerIds=${id}`).join('&')}`
  },
  getCustomerNamesWithProjectPlanningAccess() {
    return `${baseUrl()}/projects`
  },
  getCustomerFromGovAPI(orgNo: EntityId) {
    return `${baseAdminUrl()}/GetCustomerFromGovAPI/${orgNo}`
  },

  getApprovedEquipmentCustomers(customerId: EntityId) {
    return `${baseAdminUrl()}/${customerId}/approved-equipment-customers`
  },
  getCustomersForEquipmentApproval(customerId: EntityId) {
    return `${baseAdminUrl()}/${customerId}/get-customers-for-equipment-approval`
  },
  approveEquipmentCustomers(customerId: EntityId) {
    return `${baseAdminUrl()}/${customerId}/approve-equipment-customers`
  },
  deleteApproveEquipmentCustomer(customerId: EntityId) {
    return `${baseAdminUrl()}/${customerId}/delete-approved-equipment-customer`
  },
  getApprovedByCustomers(currentOwnerId: EntityId) {
    return `${baseAdminUrl()}/${currentOwnerId}/get-approved-by-customer`
  },
  getUserRoles(customerId: EntityId) {
    return `${baseUrl()}/${customerId}/user-roles`
  },
  getUserCustomersForNotification() {
    return `${baseUrl()}/all-for-notification`
  },
}

export const invalidateCustomer = {
  getCustomerAdministrators(customerId: EntityId) {
    queryClient.invalidateQueries(customerEndpoints.getCustomerAdministrators(customerId))
  },
  getCustomerLocalities() {
    queryClient.invalidateQueries(customerEndpoints.getLocalities())
  },
  getStorageCustomersByUser() {
    queryClient.invalidateQueries(customerQueryKey.GET_USER_STORAGE_CUSTOMERS())
  },
  getAdminCustomers(page: number = apiConsts.Page, pageSize: number = apiConsts.PageSize) {
    queryClient.invalidateQueries(customerEndpoints.getAdminCustomers(page, pageSize))
  },
  getCustomerUsers(customerId?: EntityId, isWithClientUserLinks?: boolean) {
    queryClient.invalidateQueries(customerQueryKey.GET_CUSTOMER_USERS(customerId, isWithClientUserLinks))
  },
  getCustomerUsersForAdmins(customerId?: EntityId, isWithClientUserLinks?: boolean) {
    queryClient.invalidateQueries(customerQueryKey.GET_CUSTOMER_USERS_FOR_ADMINS(customerId, isWithClientUserLinks))
  },
  getCustomerById(id?: EntityId) {
    queryClient.invalidateQueries(customerQueryKey.GET_CUSTOMER(id))
  },
  getLocalitiesForMap(searchValue?: string, selectedCustomer?: EntityId) {
    queryClient.invalidateQueries(customerQueryKey.GET_LOCALITIES_FOR_MAP(searchValue, selectedCustomer))
  },
  getUniqueAdminCustomers() {
    queryClient.invalidateQueries([customerEndpoints.getUniqueCustomers()])
  },
  getInvalidateCustomersServiceStationDetails() {
    queryClient.invalidateQueries([customerEndpoints.getCustomersServiceStationsAdministrators()])
  },
  getCustomersActivityCounts(customerId: EntityId) {
    queryClient.invalidateQueries([customerEndpoints.getCustomersActivityCounts(customerId)])
    queryClient.invalidateQueries({ predicate: q => (q.queryKey as any)?.[0] === 'activity-counts-batch' })
  },
  getCustomersWithAdminUserPermission() {
    queryClient.invalidateQueries([customerEndpoints.getCustomersWithAdminUserPermission()])
  },
  getCustomersNameWithProjectPlanningAccess() {
    queryClient.invalidateQueries([customerEndpoints.getCustomerNamesWithProjectPlanningAccess()])
  },
  getApprovedEquipmentCustomers(customerId: EntityId) {
    queryClient.invalidateQueries([customerEndpoints.getApprovedEquipmentCustomers(customerId)])
  },
  getCustomersForEquipmentApproval(customerId: EntityId) {
    queryClient.invalidateQueries(customerQueryKey.GET_CUSTOMERS_FOR_EQUIPMENT_APPROVAL(customerId))
  },
  getApprovedByCustomers(currentOwnerId: EntityId) {
    queryClient.invalidateQueries(customerEndpoints.getApprovedByCustomers(currentOwnerId))
  },
  getUserRoles(customerId?: EntityId) {
    queryClient.invalidateQueries(customerQueryKey.GET_USER_ROLES(customerId))
  },
}

export const useGetUserCustomers = (page: number = apiConsts.Page, pageSize: number = apiConsts.PageSize) =>
  useGet<IPaginatedResult<ICustomer>>(customerEndpoints.getUserCustomers(), { page, pageSize })

export const useGetUserCustomersForNotification = () =>
  useGet<Array<ICustomer>>(customerEndpoints.getUserCustomersForNotification())

export const useDeleteAccessBulk = (customerId: EntityId) => usePost(customerEndpoints.deleteAccessBulk(customerId))

export const useGetCustomerUsers = (customerId: EntityId, isWithClientUserLinks?: boolean, enabled?: boolean) =>
  useGet<Array<IUser>>(
    customerEndpoints.getCustomerUsers(customerId),
    { isWithClientUserLinks },
    { queryKey: customerQueryKey.GET_CUSTOMER_USERS(customerId, isWithClientUserLinks), enabled: enabled },
  )

export const useGetCustomerUsersForAdmins = (
  customerId: EntityId,
  isWithClientUserLinks?: boolean,
  searchText?: string,
  roleFilters?: number[],
) =>
  useGet<Array<IUser>>(
    customerEndpoints.getCustomerUsersForAdmins(customerId),
    { isWithClientUserLinks, searchText, roleFilters },
    {
      queryKey: customerQueryKey.GET_CUSTOMER_USERS_FOR_ADMINS(
        customerId,
        isWithClientUserLinks,
        searchText,
        roleFilters,
      ),
    },
  )

export const useGetAdminCustomers = (page?: number, pageSize?: number, month?: string) =>
  useGet<IPaginatedResult<ICustomer>>(
    customerEndpoints.getAdminCustomers(page ?? apiConsts.Page, pageSize ?? apiConsts.PageSize, month),
  )

export const useGetUniqueAdminCustomers = () => useGet<IUniqueCustomer[]>(customerEndpoints.getUniqueCustomers())

export const useGetCustomerById = (id: EntityId) => useGet<ICustomer>(customerEndpoints.getCustomerById(id))

export const useGetCustomerAdministrators = (customerId: EntityId) =>
  useGet<IUser[]>(customerEndpoints.getCustomerAdministrators(customerId))

export const useGetCustomersServiceStationsAdministrators = (searchValue?: string) =>
  useGet<ICustomer[]>(customerEndpoints.getCustomersServiceStationsAdministrators(), { searchKey: searchValue })

export const useGetUserStorageCustomers = () =>
  useGet<ICustomer[]>(
    customerEndpoints.getStorageCustomersByUser(),
    {},
    { queryKey: customerQueryKey.GET_USER_STORAGE_CUSTOMERS() },
  )

export const useGetCustomersLocalities = (searchValue: string) =>
  useGet<ICustomer[]>(customerEndpoints.getLocalities(), { search: searchValue })

export const useGetCustomersNameWithPermissions = () =>
  useGet<ICustomer[]>(customerEndpoints.getCustomersNameWithPermissions())

export const useGetCustomerNamesWithProjectPlanningAccess = () =>
  useGet<ICustomerNameWithPermission[]>(customerEndpoints.getCustomerNamesWithProjectPlanningAccess())

export const useGetCustomersLocalitiesForMap = (searchValue: string, selectedCustomer: EntityId) =>
  useGet<ILocalityMapDto[]>(
    customerEndpoints.getLocalitiesForMap(),
    {
      search: searchValue,
      customerId: selectedCustomer,
    },
    { queryKey: customerQueryKey.GET_LOCALITIES_FOR_MAP(searchValue, selectedCustomer) },
  )

export const invalidateCustomersServiceStationDetails = () =>
  queryClient.invalidateQueries(customerEndpoints.getCustomersServiceStationsAdministrators())

export const useGetCustomersActivityCounts = (
  customerId: EntityId,
  config?: { enabled?: boolean; staleTime?: number },
) => useGet<ICustomerActivitySummary[]>(customerEndpoints.getCustomersActivityCounts(customerId), undefined, config)

/** Batch activity counts for all customers (e.g. locality landing). Use for showing activity at load. */
export const useGetAllCustomersActivityCounts = (customerIds: EntityId[], options?: { staleTime?: number }) =>
  useGet<ICustomerActivityCountsBatchItem[]>(customerEndpoints.getActivityCountsBatch(customerIds), undefined, {
    queryKey: customerQueryKey.GET_ACTIVITY_COUNTS_BATCH(customerIds),
    enabled: Array.isArray(customerIds) && customerIds.length > 0,
    ...(options?.staleTime != null && { staleTime: options.staleTime }),
  })

export const useGetBulkCustomerActivityCounts = (customerIds: EntityId[]) =>
  useGet<ICustomerActivityCountsBatchItem[]>(customerEndpoints.getBulkActivityCounts(customerIds), undefined, {
    enabled: customerIds.length > 0,
  })

//Get customers of the current logged in user if there exist client admin permission
export const useGetCustomersWithAdminUserPermission = () =>
  useGet<ICustomer[]>(customerEndpoints.getCustomersWithAdminUserPermission(), undefined, { retry: 3 })

export const useGetCustomerFromGovAPI = (orgNo?: EntityId) => {
  const hasOrgNo = orgNo !== undefined && orgNo !== null && `${orgNo}`.trim().length > 0

  return useGet<IOrgCustomer>(hasOrgNo ? customerEndpoints.getCustomerFromGovAPI(orgNo) : '', undefined, {
    enabled: hasOrgNo,
  })
}

export const useGetApprovedEquipmentCustomers = (customerId: EntityId, search?: string) =>
  useGet<ICustomer[]>(customerEndpoints.getApprovedEquipmentCustomers(customerId), { search })

export const useGetCustomersForEquipmentApproval = (customerId: EntityId) =>
  useGet<ICustomer[]>(customerEndpoints.getCustomersForEquipmentApproval(customerId), undefined, {
    queryKey: customerQueryKey.GET_CUSTOMERS_FOR_EQUIPMENT_APPROVAL(customerId),
  })

export const useApproveEquipmentCustomers = (customerId: EntityId) =>
  usePost(customerEndpoints.approveEquipmentCustomers(customerId))

export const useDeleteApprovedEquipmentCustomer = (customerId: EntityId) =>
  useDelete(customerEndpoints.deleteApproveEquipmentCustomer(customerId))

export const useGetApprovedByCustomers = (currentOwnerId: EntityId) =>
  useGet<ICustomer[]>(customerEndpoints.getApprovedByCustomers(currentOwnerId))

export const useGetUserRoles = (customerId: EntityId) =>
  useGet<IRole[]>(customerEndpoints.getUserRoles(customerId), undefined, {
    queryKey: customerQueryKey.GET_USER_ROLES(customerId),
  })
